/**
 * One execution of a group, video by video (product owner, 2026-10-12): which videos it ran and how
 * each came out, so the ones it got wrong can be put side by side and read for why.
 *
 * ## Two rulers, one table
 *
 * A coherence execution is judged against truth or lie: a lie it flagged inside the lie, a lie it
 * missed, a truthful video it flagged anyway, a truthful one it left alone. A Gestos control
 * execution is judged against the labelled gestures (`GestureMatrixVideo.correct`). Both end as the
 * same row — a result, a verdict and a few counts — so the screen has one table and one set of
 * filters whichever the execution measured.
 *
 * ## Every verdict is upstream's
 *
 * Nothing is scored here: the outcome and whether the video is correct are read as upstream worked
 * them out with the execution's own threshold, under the score method the platform prefers. A video
 * it did not score — no label, a run that failed, coherence not run — has no verdict, which is never
 * «incorrecto».
 */
import type { StatusTone } from '@beonit/adaptive-raindrop';
import type {
  AnalysisGroup,
  ExecutionVideo,
  GestureMatrixVideo,
  GroupOwner,
  GroupSourceType,
  VideoLabel,
} from '../model.js';
import { controlDetailLocation } from './control-detail.js';
import { CONTROL_KIND_TITLES } from './control-groups.js';
import { notEvaluatedLabel } from './gesture-matrix-layout.js';
import { isControlOwner } from './group-owner.js';
import { type SortOption, sortOptionOf, sortRows } from './list-sort.js';
import { readingText } from './metrics-reading.js';
import { methodLabel } from './metrics-series.js';

// ── Its address ──────────────────────────────────────────────────────────────

export const EXECUTION_DETAIL_ROUTE = 'virtual-profiler-execution-detail';

/** A control's execution, under the control (`/control/:kind/executions/:batch`), since 2026-10-28. */
export const CONTROL_EXECUTION_DETAIL_ROUTE = 'virtual-profiler-control-execution-detail';

/**
 * By its owner and its label: the label is what an execution is called everywhere else. A group's
 * is under the group, by its id; a control's under the control, by its kind — a control has no id.
 */
export function executionDetailLocation(
  owner: GroupOwner,
  batch: string,
): {
  name: string;
  params: { groupId: string; batch: string } | { kind: string; batch: string };
} {
  if (isControlOwner(owner)) {
    return {
      name: CONTROL_EXECUTION_DETAIL_ROUTE,
      params: { ...controlDetailLocation(owner.controlKind).params, batch },
    };
  }

  return {
    name: EXECUTION_DETAIL_ROUTE,
    params: { groupId: String(owner.groupId), batch },
  };
}

/**
 * Where «back» goes: a control's execution is opened from the control, any other from its group —
 * once the group is read, since its name is the label. Before that, or with no owner at all, it is
 * the board, which holds both.
 */
export function executionBack(
  owner: GroupOwner | null,
  group?: Pick<AnalysisGroup, 'id' | 'name'>,
): {
  label: string;
  to: { name: string; params?: Record<string, string> };
} {
  if (owner && isControlOwner(owner)) {
    return {
      label: CONTROL_KIND_TITLES[owner.controlKind],
      to: controlDetailLocation(owner.controlKind),
    };
  }
  if (!owner || !group || group.id !== owner.groupId) {
    return {
      label: 'Control',
      to: { name: 'virtual-profiler-analysis-groups' },
    };
  }

  return {
    label: group.name,
    to: {
      name: 'virtual-profiler-group-detail',
      params: { groupId: String(group.id) },
    },
  };
}

// ── The rows ─────────────────────────────────────────────────────────────────

/** Whether the execution got the video right; `not_scored` is neither. */
export type ExecutionVerdict = 'correct' | 'wrong' | 'not_scored';

export const VERDICT_LABELS: Readonly<Record<ExecutionVerdict, string>> = Object.freeze({
  wrong: 'Incorrectos',
  correct: 'Correctos',
  not_scored: 'Sin medir',
});

export interface ExecutionVideoRow {
  key: string;
  sourceType: GroupSourceType;
  sourceId: number;
  name: string;
  /** Deleted from the dataset after the execution closed: struck through, figures intact. */
  deleted: boolean;
  label: VideoLabel | null;
  /** The run it was read by, for the link to what the execution saw; null without one. */
  runId: string | null;
  /** What happened, in a chip: «Mentira no detectada». */
  result: { key: string; label: string; tone: StatusTone };
  verdict: ExecutionVerdict;
  /** One per column of the mode (`EXECUTION_COLUMNS`), already worded; «—» where there is none. */
  cells: string[];
}

export type ExecutionMode = 'coherence' | 'gestures';

/** The counts beside the verdict, by what the execution measured. */
export const EXECUTION_COLUMNS: Readonly<Record<ExecutionMode, readonly string[]>> = Object.freeze({
  coherence: ['Ideas con alerta', 'Dentro de la mentira', 'Fuera de la mentira'],
  gestures: ['Gestos etiquetados', 'Detectados', 'Detecciones', 'Falsos positivos'],
});

const NONE = '—';

const count = (value: number | null): string => (value === null ? NONE : String(value));

function nameOf(video: { name: string | null; sourceId: number }): string {
  return video.name ?? `Vídeo #${video.sourceId}`;
}

/** Upstream's four outcomes, in the product's words. An outcome it adds tomorrow is shown as it is. */
const OUTCOMES: Readonly<Record<string, { label: string; tone: StatusTone }>> = Object.freeze({
  incoherent_detected: { label: 'Mentira detectada', tone: 'success' },
  incoherent_missed: { label: 'Mentira no detectada', tone: 'danger' },
  false_positive: { label: 'Falso positivo', tone: 'warning' },
  truthful_cleared: { label: 'Verdad sin alertas', tone: 'success' },
});

/** Why a video has no verdict, by upstream's word for it. */
const LEFT_OUT: Readonly<Record<string, string>> = Object.freeze({
  not_analysed: 'Falló o se canceló',
  pending: 'En marcha',
  never_run: 'No está en la ejecución',
  pinned_missing: 'Sin su análisis',
  missing: 'Sin resultado de veracidad',
  not_requested: 'Veracidad no ejecutada',
});

function leftOut(video: ExecutionVideo): ExecutionVideoRow['result'] {
  if (video.reason !== null) {
    return {
      key: video.reason,
      label: LEFT_OUT[video.reason] ?? `Sin medir («${video.reason}»)`,
      tone: 'neutral',
    };
  }

  return video.label === null
    ? { key: 'unlabelled', label: 'Sin etiquetar', tone: 'neutral' }
    : { key: 'not_scored', label: 'Sin medir', tone: 'neutral' };
}

/**
 * A coherence execution's videos under one score method. A scored video with no result to read
 * (`missing`) counts against the execution upstream, so it is wrong here too; one whose run did not
 * run coherence is in no figure, and has no verdict.
 */
export function coherenceRows(
  videos: readonly ExecutionVideo[],
  method: string,
): ExecutionVideoRow[] {
  return videos.map((video) => {
    const base = {
      key: `${video.sourceType}:${video.sourceId}`,
      sourceType: video.sourceType,
      sourceId: video.sourceId,
      name: nameOf(video),
      deleted: false,
      label: video.label,
      runId: video.runId,
    };
    const score = video.methods[method];

    if (!score || video.coherenceStatus === 'not_requested') {
      const reason = score ? { ...video, reason: 'not_requested' } : video;

      return {
        ...base,
        result: leftOut(reason),
        verdict: 'not_scored',
        cells: [NONE, NONE, NONE],
      };
    }

    if (video.coherenceStatus === 'missing') {
      return {
        ...base,
        result: { key: 'missing', label: LEFT_OUT.missing!, tone: 'warning' },
        verdict: 'wrong',
        cells: [NONE, NONE, NONE],
      };
    }

    const outcome = OUTCOMES[score.outcome] ?? {
      label: score.outcome,
      tone: 'neutral' as const,
    };

    return {
      ...base,
      result: { key: score.outcome, ...outcome },
      verdict: score.correct === true ? 'correct' : 'wrong',
      cells: [
        String(score.subtractingIdeas),
        score.overlappingIdeas === null
          ? NONE
          : score.incoherenceIdeas === null
            ? String(score.overlappingIdeas)
            : `${score.overlappingIdeas} de ${score.incoherenceIdeas}`,
        String(score.falsePositiveIdeas),
      ],
    };
  });
}

/** A Gestos control execution's videos, by the control's own rule for one video. */
export function gestureRows(videos: readonly GestureMatrixVideo[]): ExecutionVideoRow[] {
  return videos.map((video) => {
    const scored = video.evaluated && video.correct !== null;

    return {
      key: `${video.sourceType}:${video.sourceId}`,
      sourceType: video.sourceType,
      sourceId: video.sourceId,
      name: nameOf(video),
      deleted: video.sourceDeleted === true,
      label: null,
      runId: null,
      result: scored
        ? video.correct
          ? {
              key: 'gestures_correct',
              label: 'Gestos correctos',
              tone: 'success',
            }
          : {
              key: 'gestures_wrong',
              label: 'Gestos incorrectos',
              tone: 'danger',
            }
        : {
            key: video.notEvaluatedReason ?? 'not_evaluated',
            label: notEvaluatedLabel(video.notEvaluatedReason),
            tone: 'neutral',
          },
      verdict: scored ? (video.correct ? 'correct' : 'wrong') : 'not_scored',
      cells: [
        count(video.labels),
        count(video.labelsDetected),
        count(video.detections),
        count(video.falsePositives),
      ],
    };
  });
}

/** How many of each verdict: the quick filters' counts, and the page's summary. */
export function verdictCounts(
  rows: readonly ExecutionVideoRow[],
): Record<ExecutionVerdict, number> {
  const counts: Record<ExecutionVerdict, number> = {
    wrong: 0,
    correct: 0,
    not_scored: 0,
  };
  for (const row of rows) counts[row.verdict] += 1;

  return counts;
}

// ── Search and filters ───────────────────────────────────────────────────────

export type ExecutionLabelFilter = VideoLabel | 'unlabelled';

export const EXECUTION_LABEL_FILTERS: ReadonlyArray<{
  value: ExecutionLabelFilter;
  label: string;
}> = Object.freeze([
  { value: 'incoherent', label: 'Mentira' },
  { value: 'coherent', label: 'Verdad' },
  { value: 'unlabelled', label: 'Sin etiquetar' },
]);

export interface ExecutionVideoFilters {
  search: string;
  /** The quick filter: one verdict at a time, or none. */
  verdict: ExecutionVerdict | null;
  results: string[];
  labels: ExecutionLabelFilter[];
}

export function emptyExecutionVideoFilters(): ExecutionVideoFilters {
  return { search: '', verdict: null, results: [], labels: [] };
}

/** The funnel's badge. The search and the quick filter are in sight already. */
export function activeExecutionVideoFilterCount(filters: ExecutionVideoFilters): number {
  return (filters.results.length > 0 ? 1 : 0) + (filters.labels.length > 0 ? 1 : 0);
}

/** The results these rows have, in the order they first appear: the funnel offers no empty one. */
export function resultOptions(
  rows: readonly ExecutionVideoRow[],
): Array<{ value: string; label: string }> {
  const seen = new Map<string, string>();
  for (const row of rows) if (!seen.has(row.result.key)) seen.set(row.result.key, row.result.label);

  return [...seen].map(([value, label]) => ({ value, label }));
}

const fold = (text: string): string =>
  text
    .normalize('NFD')
    .replaceAll(/\p{Diacritic}/gu, '')
    .toLowerCase();

export function filterExecutionVideos(
  rows: readonly ExecutionVideoRow[],
  filters: ExecutionVideoFilters,
): ExecutionVideoRow[] {
  const term = fold(filters.search.trim());

  return rows.filter(
    (row) =>
      (term === '' || fold(row.name).includes(term) || String(row.sourceId) === term) &&
      (filters.verdict === null || row.verdict === filters.verdict) &&
      (filters.results.length === 0 || filters.results.includes(row.result.key)) &&
      (filters.labels.length === 0 || filters.labels.includes(row.label ?? 'unlabelled')),
  );
}

// ── Order ────────────────────────────────────────────────────────────────────

export type ExecutionVideoSortField = 'verdict' | 'name' | 'result' | 'label';

/** The failures first by default: they are what the page is opened for. */
export const EXECUTION_VIDEO_SORTS: readonly SortOption<ExecutionVideoSortField>[] = Object.freeze([
  {
    key: 'verdict:asc',
    label: 'Los incorrectos primero',
    orderBy: 'verdict',
    orderDir: 'asc',
  },
  {
    key: 'verdict:desc',
    label: 'Los correctos primero',
    orderBy: 'verdict',
    orderDir: 'desc',
  },
  {
    key: 'name:asc',
    label: 'Nombre (A-Z)',
    orderBy: 'name',
    orderDir: 'asc',
  },
  {
    key: 'name:desc',
    label: 'Nombre (Z-A)',
    orderBy: 'name',
    orderDir: 'desc',
  },
  {
    key: 'result:asc',
    label: 'Por resultado',
    orderBy: 'result',
    orderDir: 'asc',
  },
  {
    key: 'label:asc',
    label: 'Por etiqueta',
    orderBy: 'label',
    orderDir: 'asc',
  },
] satisfies SortOption<ExecutionVideoSortField>[]);

/** What nobody scored sits between the two verdicts, in either direction. */
const VERDICT_RANK: Readonly<Record<ExecutionVerdict, number>> = Object.freeze({
  wrong: 0,
  not_scored: 1,
  correct: 2,
});

const LABEL_RANK: Readonly<Record<ExecutionLabelFilter, number>> = Object.freeze({
  incoherent: 0,
  coherent: 1,
  unlabelled: 2,
});

export function sortExecutionVideos(
  rows: readonly ExecutionVideoRow[],
  key: string,
): ExecutionVideoRow[] {
  // By name first, so the rows that tie on the order asked for read alphabetically (the sort is stable).
  const byName = sortRows(rows, EXECUTION_VIDEO_SORTS[2]!, (row) => row.name);

  return sortRows(byName, sortOptionOf(EXECUTION_VIDEO_SORTS, key), (row, field) => {
    switch (field) {
      case 'verdict':
        return VERDICT_RANK[row.verdict];
      case 'result':
        return row.result.label;
      case 'label':
        return LABEL_RANK[row.label ?? 'unlabelled'];
      default:
        return row.name;
    }
  });
}

/**
 * What a coherence execution's figures are read with: «Método Alerta mayor · Métricas globales ·
 * umbral 80 %» — the user's preferred method and reading, and the threshold it ran with.
 */
export function executionReadingLine(
  method: string,
  global: boolean,
  thresholdPct: number | null | undefined,
): string {
  const threshold =
    thresholdPct === null || thresholdPct === undefined ? '' : ` · umbral ${thresholdPct} %`;

  return `Método ${readingText(method, global)}${threshold}`;
}

// ── CSV ──────────────────────────────────────────────────────────────────────

const LABEL_TEXT: Readonly<Record<ExecutionLabelFilter, string>> = Object.freeze({
  incoherent: 'Mentira',
  coherent: 'Verdad',
  unlabelled: 'Sin etiquetar',
});

const VERDICT_TEXT: Readonly<Record<ExecutionVerdict, string>> = Object.freeze({
  correct: 'Correcto',
  wrong: 'Incorrecto',
  not_scored: 'Sin medir',
});

export function verdictText(verdict: ExecutionVerdict): string {
  return VERDICT_TEXT[verdict];
}

/** What is on screen, as a sheet: the failed videos of two executions are compared outside too. */
export function executionVideosCsv(
  rows: readonly ExecutionVideoRow[],
  mode: ExecutionMode,
  /** The score method the coherence rows were read with: a sheet that leaves the screen says it. */
  method?: string,
): { headers: string[]; cells: (string | number)[][] } {
  const withLabel = mode === 'coherence';
  const withMethod = withLabel && method !== undefined;

  return {
    headers: [
      'ID',
      'Vídeo',
      ...(withLabel ? ['Etiqueta'] : []),
      ...(withMethod ? ['Método'] : []),
      'Resultado',
      'Correcto',
      ...EXECUTION_COLUMNS[mode],
    ],
    cells: rows.map((row) => [
      row.sourceId,
      row.name,
      ...(withLabel ? [LABEL_TEXT[row.label ?? 'unlabelled']] : []),
      ...(withMethod ? [methodLabel(method)] : []),
      row.result.label,
      VERDICT_TEXT[row.verdict],
      ...row.cells.map((cell) => (cell === NONE ? '' : cell)),
    ]),
  };
}
